import { Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import yn from "yn";
import { ManifoldNextGenAccountMenu } from "~/components/ManifoldNextGenAccountMenu";
import { ManifoldNextGenHelpPopover } from "~/components/ManifoldNextGenHelpPopover";
import { ManifoldNextGenNavPicker } from "~/components/ManifoldNextGenNavPicker";
import { NotificationBell } from "~/components/NotificationBell";
import { BannerAppearanceShortcut } from "~/components/hero-background/BannerAppearanceShortcut";
import { HeroBackground } from "~/components/hero-background/HeroBackground";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useHeroBackground } from "~/hooks/useHeroBackground";
import type { ResolvedTheme } from "~/hooks/useNextGenTheme";
import { cn } from "~/lib/utils";
import { ROUTE_NOVA_VIEW } from "~/router/constants";

interface ManifoldNextGenAppHeaderProps {
  onSwitchToClassic?: () => void;
  canGoBack?: boolean;
  // What the app's theme resolves to, for the notification inbox — Courier
  // renders it and can't read the `.dark` class itself.
  resolvedTheme?: ResolvedTheme;
}

export function ManifoldNextGenAppHeader({
  onSwitchToClassic,
  canGoBack = true,
  resolvedTheme,
}: ManifoldNextGenAppHeaderProps) {
  const { breadcrumbs, title, actions, rightPane, variant, heroContent, onBack } =
    useNextGenHeader();
  const navigate = useNavigate();
  // Set per environment in the build's env file; off wherever it isn't set.
  const notificationsEnabled = yn(import.meta.env.VITE_NOTIFICATIONS_ENABLED, { default: false });
  const colored = variant === "colored";
  const { heroBackground } = useHeroBackground();
  const handleBack = canGoBack ? onBack : () => navigate(ROUTE_NOVA_VIEW);

  // nav-picker (auto) | breadcrumbs (1fr) | title (auto) | actions+avatar (auto) | optional rightPane
  const gridCols = rightPane
    ? "grid-cols-[auto_1fr_auto_auto_auto]"
    : "grid-cols-[auto_1fr_auto_auto]";

  return (
    <div
      className={cn(
        "relative transition-colors duration-300",
        colored ? "bg-[var(--dt-primitives-color-brand-deep-space)]" : "bg-transparent"
      )}
    >
      {colored && (
        // Clipped in its own layer: `overflow-hidden` on the wrapper would also
        // clip the search dropdown inside `heroContent`.
        <HeroBackground theme={heroBackground} quietCenter />
      )}
      <div
        data-testid="nextgen-app-header"
        className={cn(
          "relative grid shrink-0 items-stretch border-b",
          gridCols,
          colored ? "border-transparent bg-transparent" : "border-border bg-sidebar"
        )}
      >
        <div className="flex items-center gap-1 pl-3">
          <ManifoldNextGenNavPicker colored={colored} />
          {colored && <BannerAppearanceShortcut />}
        </div>

        <div className="flex items-center py-2 pl-2">
          {onBack && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              aria-label="Back"
              className="mr-1 size-8 shrink-0"
            >
              <ArrowLeft className="size-4" />
            </Button>
          )}
          {breadcrumbs.length > 0 ? (
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;
                  return (
                    <Fragment key={index}>
                      <BreadcrumbItem>
                        {isLast || !crumb.href ? (
                          <BreadcrumbPage className="text-sm font-medium text-foreground">
                            {crumb.label}
                          </BreadcrumbPage>
                        ) : (
                          /* Styled here rather than on the `Link`: `asChild`
                             renders a Radix `Slot`, which string-joins the two
                             classNames instead of running them through `cn`, so
                             a utility set on the child only collides with the
                             primitive's. */
                          <BreadcrumbLink
                            asChild
                            className="font-medium text-foreground opacity-70 hover:opacity-100"
                          >
                            <Link to={crumb.href}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                      {!isLast && <BreadcrumbSeparator />}
                    </Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          ) : (
            title && <span className="px-2 text-sm font-medium text-foreground">{title}</span>
          )}
        </div>

        <div className="flex items-center justify-center px-4 py-2">
          {breadcrumbs.length > 0 && title && (
            <span className="text-sm font-medium text-foreground">{title}</span>
          )}
        </div>

        <div className="flex items-center gap-2 py-2 pr-4">
          {actions[0]}
          {actions[1]}
          {notificationsEnabled && (
            <NotificationBell colored={colored} resolvedTheme={resolvedTheme} />
          )}
          <ManifoldNextGenHelpPopover colored={colored} />
          <ManifoldNextGenAccountMenu onSwitchToClassic={onSwitchToClassic} colored={colored} />
        </div>
        {rightPane}
      </div>
      {/* Positioned so it paints above the background layers. */}
      {colored && <div className="relative">{heroContent}</div>}
    </div>
  );
}
